import React, { useState, useMemo } from "react";
import {
  FileText, Download, Calendar, Users, CheckCircle,
  AlertTriangle, Clock, ChevronRight, Printer,
} from "lucide-react";

export default function ReportsTab({ C, allNotes, teamMembers, sharedIndividuals, safetyAlerts, orgId }) {
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0); // 0 = this week, -1 = last week, etc.
  const [generating, setGenerating] = useState(false);

  // Week date range
  const weekRange = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - start.getDay() + (weekOffset * 7));
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end, label: `${start.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` };
  }, [weekOffset]);

  // Filter notes for the week
  const weekNotes = useMemo(() => {
    return allNotes.filter(n => {
      const d = n.createdAt?.toDate ? n.createdAt.toDate() : new Date(n.createdAt || n.date || 0);
      return d >= weekRange.start && d <= weekRange.end;
    });
  }, [allNotes, weekRange]);

  // Week alerts
  const weekAlerts = useMemo(() => {
    return (safetyAlerts || []).filter(a => {
      const d = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
      return d >= weekRange.start && d <= weekRange.end;
    });
  }, [safetyAlerts, weekRange]);

  // Per-agent stats for the week
  const agentStats = useMemo(() => {
    const agents = teamMembers.filter(m => m.role === "agent" || m.role === "counselor");
    return agents.map(agent => {
      const notes = weekNotes.filter(n => n.agentId === agent.id);
      const signed = notes.filter(n => n.supervisorReviewed).length;
      const flagged = notes.filter(n => n.hasPriorityFlags).length;
      const alerts = weekAlerts.filter(a => a.agentId === agent.id);
      const individuals = new Set(notes.map(n => n.individualName)).size;
      const totalUnits = notes.reduce((s, n) => {
        const match = (n.billingUnits || "").match(/(\d+)/);
        return s + (match ? parseInt(match[1]) : 1);
      }, 0);

      return {
        agent,
        notes,
        signed,
        unsigned: notes.length - signed,
        flagged,
        alerts,
        individuals,
        totalUnits,
      };
    }).sort((a, b) => b.notes.length - a.notes.length);
  }, [teamMembers, weekNotes, weekAlerts]);

  // Org-wide week summary
  const orgSummary = useMemo(() => ({
    totalNotes: weekNotes.length,
    signed: weekNotes.filter(n => n.supervisorReviewed).length,
    flagged: weekNotes.filter(n => n.hasPriorityFlags).length,
    alerts: weekAlerts.length,
    agents: new Set(weekNotes.map(n => n.agentId)).size,
    individuals: new Set(weekNotes.map(n => n.individualName)).size,
    totalUnits: weekNotes.reduce((s, n) => {
      const match = (n.billingUnits || "").match(/(\d+)/);
      return s + (match ? parseInt(match[1]) : 1);
    }, 0),
  }), [weekNotes, weekAlerts]);

  // Generate PDF for a single agent
  const generateAgentPDF = (agentData) => {
    setGenerating(true);
    const { agent, notes, signed, unsigned, flagged, alerts, individuals, totalUnits } = agentData;

    const noteRows = notes.map(n => {
      const date = n.createdAt?.toDate ? n.createdAt.toDate().toLocaleDateString() : n.date || "—";
      const svc = n.serviceType || "ICM";
      const ind = n.individualName || "—";
      const status = n.supervisorReviewed ? "✓ Signed" : "Pending";
      const flag = n.hasPriorityFlags ? "⚠ YES" : "—";
      const units = (n.billingUnits || "").match(/(\d+)/)?.[1] || "1";
      return `<tr>
        <td style="padding:5px 8px;border:1px solid #ddd;font-size:10px">${date}</td>
        <td style="padding:5px 8px;border:1px solid #ddd;font-size:10px">${ind}</td>
        <td style="padding:5px 8px;border:1px solid #ddd;font-size:10px">${svc}</td>
        <td style="padding:5px 8px;border:1px solid #ddd;font-size:10px">${units}</td>
        <td style="padding:5px 8px;border:1px solid #ddd;font-size:10px;color:${n.supervisorReviewed ? "#276749" : "#c05621"}">${status}</td>
        <td style="padding:5px 8px;border:1px solid #ddd;font-size:10px;color:${n.hasPriorityFlags ? "#c53030" : "#718096"}">${flag}</td>
      </tr>`;
    }).join("");

    const alertRows = alerts.map(a => {
      const date = a.createdAt?.toDate ? a.createdAt.toDate().toLocaleDateString() : "—";
      return `<tr>
        <td style="padding:5px 8px;border:1px solid #ddd;font-size:10px">${date}</td>
        <td style="padding:5px 8px;border:1px solid #ddd;font-size:10px">${a.individualName || "—"}</td>
        <td style="padding:5px 8px;border:1px solid #ddd;font-size:10px;color:#c53030;font-weight:bold">${a.category || "Safety"}</td>
        <td style="padding:5px 8px;border:1px solid #ddd;font-size:10px;font-style:italic">"${a.triggerPhrase || "—"}"</td>
        <td style="padding:5px 8px;border:1px solid #ddd;font-size:10px">${a.status || "—"}</td>
      </tr>`;
    }).join("");

    const signRate = notes.length > 0 ? Math.round((signed / notes.length) * 100) : 0;

    const html = `<!DOCTYPE html>
<html><head><title>Weekly Report — ${agent.displayName || agent.email}</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 11px; color: #1a202c; margin: 40px; }
  h1 { font-size: 18px; color: #1a365d; border-bottom: 2px solid #2b6cb0; padding-bottom: 6px; margin-bottom: 4px; }
  h2 { font-size: 13px; color: #2b6cb0; margin-top: 20px; margin-bottom: 6px; }
  .subtitle { font-size: 11px; color: #718096; margin-bottom: 16px; }
  .stats { display: flex; gap: 16px; margin: 16px 0; }
  .stat { background: #f7fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 16px; text-align: center; flex: 1; }
  .stat-value { font-size: 22px; font-weight: 900; color: #2b6cb0; }
  .stat-label { font-size: 9px; color: #718096; text-transform: uppercase; letter-spacing: 1px; margin-top: 2px; }
  table { border-collapse: collapse; width: 100%; margin-top: 6px; }
  th { background: #edf2f7; padding: 5px 8px; border: 1px solid #ddd; font-size: 10px; text-align: left; }
  .sign-bar { height: 8px; background: #edf2f7; border-radius: 4px; margin-top: 8px; }
  .sign-fill { height: 100%; border-radius: 4px; }
  .footer { text-align: center; color: #718096; font-size: 9px; margin-top: 30px; border-top: 1px solid #e2e8f0; padding-top: 10px; }
  .alert-box { background: #fff5f5; border: 1px solid #fed7d7; border-radius: 6px; padding: 8px 12px; margin-top: 8px; }
  @media print { body { margin: 20px; } }
</style>
</head><body>

<h1>Weekly Agent Report</h1>
<div class="subtitle">
  <strong>${agent.displayName || agent.email}</strong> · ${agent.credentials || agent.role || "Agent"} · ${agent.program || "—"}<br/>
  Week of ${weekRange.label} · Organization: ${orgId}
</div>

<div class="stats">
  <div class="stat"><div class="stat-value">${notes.length}</div><div class="stat-label">Notes</div></div>
  <div class="stat"><div class="stat-value">${individuals}</div><div class="stat-label">Individuals</div></div>
  <div class="stat"><div class="stat-value">${totalUnits}</div><div class="stat-label">Units</div></div>
  <div class="stat"><div class="stat-value">${signRate}%</div><div class="stat-label">Co-Sign Rate</div></div>
</div>

<div style="margin:12px 0">
  <div style="font-size:10px;color:#4a5568;margin-bottom:4px">Co-Sign Progress: ${signed}/${notes.length} notes signed</div>
  <div class="sign-bar">
    <div class="sign-fill" style="width:${signRate}%;background:${signRate === 100 ? "#48bb78" : signRate > 50 ? "#ecc94b" : "#fc8181"}"></div>
  </div>
</div>

${unsigned > 0 ? `<div class="alert-box" style="margin-bottom:12px">
  <strong style="color:#c53030">⚠ ${unsigned} unsigned note${unsigned !== 1 ? "s" : ""}</strong>
  <span style="color:#718096"> — Cannot be submitted for Medicaid billing until co-signed.</span>
</div>` : ""}

<h2>Session Log (${notes.length} Notes)</h2>
${notes.length > 0 ? `<table>
  <tr><th>Date</th><th>Individual</th><th>Service</th><th>Units</th><th>Status</th><th>Flags</th></tr>
  ${noteRows}
</table>` : "<p style='color:#718096'>No sessions this week.</p>"}

${alerts.length > 0 ? `<h2>Safety Alerts (${alerts.length})</h2>
<table>
  <tr><th>Date</th><th>Individual</th><th>Category</th><th>Trigger</th><th>Status</th></tr>
  ${alertRows}
</table>` : ""}

<div class="footer">
  Generated by FieldNotes · ${new Date().toLocaleString()}<br/>
  CONFIDENTIAL — For internal supervisory review only
</div>

</body></html>`;

    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
    w.onload = () => w.print();
    setGenerating(false);
  };

  // Generate all-agent summary PDF
  const generateOrgPDF = () => {
    setGenerating(true);

    const agentRows = agentStats.map(a => {
      const signRate = a.notes.length > 0 ? Math.round((a.signed / a.notes.length) * 100) : 0;
      return `<tr>
        <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;font-weight:bold">${a.agent.displayName || a.agent.email}</td>
        <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px">${a.agent.role}</td>
        <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;text-align:center">${a.notes.length}</td>
        <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;text-align:center">${a.individuals}</td>
        <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;text-align:center">${a.totalUnits}</td>
        <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;text-align:center;color:${signRate === 100 ? "#276749" : signRate > 50 ? "#c05621" : "#c53030"}">${signRate}%</td>
        <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;text-align:center;color:${a.flagged > 0 ? "#c53030" : "#718096"}">${a.flagged}</td>
        <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;text-align:center;color:${a.alerts.length > 0 ? "#c53030" : "#718096"}">${a.alerts.length}</td>
      </tr>`;
    }).join("");

    const html = `<!DOCTYPE html>
<html><head><title>Weekly Organization Report — ${weekRange.label}</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 11px; color: #1a202c; margin: 40px; }
  h1 { font-size: 18px; color: #1a365d; border-bottom: 2px solid #2b6cb0; padding-bottom: 6px; margin-bottom: 4px; }
  h2 { font-size: 13px; color: #2b6cb0; margin-top: 20px; margin-bottom: 6px; }
  .subtitle { font-size: 11px; color: #718096; margin-bottom: 16px; }
  .stats { display: flex; gap: 12px; margin: 16px 0; }
  .stat { background: #f7fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 14px; text-align: center; flex: 1; }
  .stat-value { font-size: 20px; font-weight: 900; color: #2b6cb0; }
  .stat-label { font-size: 8px; color: #718096; text-transform: uppercase; letter-spacing: 1px; margin-top: 2px; }
  table { border-collapse: collapse; width: 100%; margin-top: 6px; }
  th { background: #edf2f7; padding: 6px 8px; border: 1px solid #ddd; font-size: 10px; text-align: left; }
  .footer { text-align: center; color: #718096; font-size: 9px; margin-top: 30px; border-top: 1px solid #e2e8f0; padding-top: 10px; }
  @media print { body { margin: 20px; } }
</style>
</head><body>

<h1>Weekly Organization Report</h1>
<div class="subtitle">${orgId} · Week of ${weekRange.label}</div>

<div class="stats">
  <div class="stat"><div class="stat-value">${orgSummary.totalNotes}</div><div class="stat-label">Total Notes</div></div>
  <div class="stat"><div class="stat-value">${orgSummary.agents}</div><div class="stat-label">Active Agents</div></div>
  <div class="stat"><div class="stat-value">${orgSummary.individuals}</div><div class="stat-label">Individuals Seen</div></div>
  <div class="stat"><div class="stat-value">${orgSummary.totalUnits}</div><div class="stat-label">Total Units</div></div>
  <div class="stat"><div class="stat-value">${orgSummary.signed}</div><div class="stat-label">Co-Signed</div></div>
  <div class="stat"><div class="stat-value" style="color:${orgSummary.alerts > 0 ? "#c53030" : "#276749"}">${orgSummary.alerts}</div><div class="stat-label">Safety Alerts</div></div>
</div>

<h2>Agent Performance</h2>
<table>
  <tr><th>Agent</th><th>Role</th><th style="text-align:center">Notes</th><th style="text-align:center">Individuals</th><th style="text-align:center">Units</th><th style="text-align:center">Co-Sign %</th><th style="text-align:center">Flags</th><th style="text-align:center">Alerts</th></tr>
  ${agentRows}
</table>

${agentStats.filter(a => a.unsigned > 0).length > 0 ? `
<h2>⚠ Unsigned Notes Requiring Attention</h2>
<table>
  <tr><th>Agent</th><th>Unsigned</th><th>Individuals Affected</th></tr>
  ${agentStats.filter(a => a.unsigned > 0).map(a => {
    const unsignedInds = [...new Set(a.notes.filter(n => !n.supervisorReviewed).map(n => n.individualName))].join(", ");
    return `<tr>
      <td style="padding:5px 8px;border:1px solid #ddd;font-size:11px">${a.agent.displayName || a.agent.email}</td>
      <td style="padding:5px 8px;border:1px solid #ddd;font-size:11px;color:#c53030;font-weight:bold">${a.unsigned}</td>
      <td style="padding:5px 8px;border:1px solid #ddd;font-size:11px">${unsignedInds}</td>
    </tr>`;
  }).join("")}
</table>` : ""}

<div class="footer">
  Generated by FieldNotes · ${new Date().toLocaleString()}<br/>
  CONFIDENTIAL — For internal supervisory review only
</div>

</body></html>`;

    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
    w.onload = () => w.print();
    setGenerating(false);
  };

  return (
    <div>
      {/* Week Selector */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {[
            { offset: 0, label: "This Week" },
            { offset: -1, label: "Last Week" },
            { offset: -2, label: "2 Weeks Ago" },
            { offset: -3, label: "3 Weeks Ago" },
          ].map(w => (
            <button key={w.offset} onClick={() => setWeekOffset(w.offset)} style={{
              padding: "5px 12px", borderRadius: 8, fontSize: 10, fontWeight: 700, cursor: "pointer",
              background: weekOffset === w.offset ? "rgba(49,130,206,0.2)" : "rgba(15,23,42,0.5)",
              color: weekOffset === w.offset ? "#63b3ed" : "#718096",
              border: `1px solid ${weekOffset === w.offset ? "rgba(49,130,206,0.3)" : "rgba(99,179,237,0.06)"}`,
            }}>{w.label}</button>
          ))}
        </div>
      </div>

      <div style={{ fontSize: 11, color: "#718096", marginBottom: 16 }}>
        <Calendar size={12} style={{ display: "inline", marginRight: 4 }} />
        {weekRange.label}
      </div>

      {/* Org Summary */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 16 }}>
        {[
          { label: "Notes", value: orgSummary.totalNotes, color: "#4fd1c5" },
          { label: "Individuals", value: orgSummary.individuals, color: "#63b3ed" },
          { label: "Units", value: orgSummary.totalUnits, color: "#b794f4" },
          { label: "Co-Signed", value: orgSummary.signed, color: "#68d391" },
          { label: "Flagged", value: orgSummary.flagged, color: orgSummary.flagged > 0 ? "#fc8181" : "#68d391" },
          { label: "Alerts", value: orgSummary.alerts, color: orgSummary.alerts > 0 ? "#fc8181" : "#68d391" },
        ].map((s, i) => (
          <div key={i} style={{ ...C.card, textAlign: "center", padding: 12, marginBottom: 0 }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: s.color, fontFamily: "monospace" }}>{s.value}</div>
            <div style={{ fontSize: 8, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Org Report Button */}
      <button onClick={generateOrgPDF} disabled={generating} style={{
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        width: "100%", padding: "12px 0", borderRadius: 12, fontSize: 13, fontWeight: 800,
        background: "linear-gradient(135deg, #3182ce, #2b6cb0)", color: "white",
        border: "none", cursor: "pointer", marginBottom: 20, opacity: generating ? 0.5 : 1,
      }}>
        <Printer size={16} /> Generate Org-Wide Weekly Report
      </button>

      {/* Per-Agent Cards */}
      <div style={{ fontSize: 11, fontWeight: 800, color: "#63b3ed", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 10 }}>
        Agent Reports
      </div>

      {agentStats.length === 0 ? (
        <div style={{ ...C.card, textAlign: "center", padding: "32px 0" }}>
          <Users size={28} style={{ color: "#2d3748", margin: "0 auto 8px" }} />
          <div style={{ fontSize: 13, color: "#4a5568" }}>No agent activity this week</div>
        </div>
      ) : (
        agentStats.map(a => {
          const signRate = a.notes.length > 0 ? Math.round((a.signed / a.notes.length) * 100) : 0;
          return (
            <div key={a.agent.id} style={{ ...C.card, marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10,
                    background: "linear-gradient(135deg, #63b3ed, #4fd1c5)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 14, fontWeight: 800, color: "white", flexShrink: 0,
                  }}>
                    {(a.agent.displayName || a.agent.email || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800 }}>{a.agent.displayName || a.agent.email}</div>
                    <div style={{ fontSize: 10, color: "#718096" }}>
                      {a.notes.length} notes · {a.individuals} individuals · {a.totalUnits} units
                    </div>
                  </div>
                </div>
                <button onClick={() => generateAgentPDF(a)} style={{
                  display: "flex", alignItems: "center", gap: 4,
                  padding: "6px 12px", borderRadius: 8, fontSize: 10, fontWeight: 700, cursor: "pointer",
                  background: "rgba(104,211,145,0.08)", color: "#68d391",
                  border: "1px solid rgba(104,211,145,0.15)",
                }}>
                  <Download size={11} /> PDF
                </button>
              </div>

              {/* Stats row */}
              <div style={{ display: "flex", gap: 12, marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(99,179,237,0.04)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <CheckCircle size={10} style={{ color: "#68d391" }} />
                  <span style={{ fontSize: 10, color: "#718096" }}>{signRate}% signed</span>
                </div>
                {a.unsigned > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <Clock size={10} style={{ color: "#ecc94b" }} />
                    <span style={{ fontSize: 10, color: "#ecc94b" }}>{a.unsigned} unsigned</span>
                  </div>
                )}
                {a.flagged > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <AlertTriangle size={10} style={{ color: "#fc8181" }} />
                    <span style={{ fontSize: 10, color: "#fc8181" }}>{a.flagged} flagged</span>
                  </div>
                )}
                {a.alerts.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <AlertTriangle size={10} style={{ color: "#fc8181" }} />
                    <span style={{ fontSize: 10, color: "#fc8181" }}>{a.alerts.length} alerts</span>
                  </div>
                )}
              </div>

              {/* Sign rate bar */}
              <div style={{ height: 3, borderRadius: 2, background: "rgba(99,179,237,0.06)", marginTop: 8 }}>
                <div style={{
                  height: "100%", borderRadius: 2, width: `${signRate}%`,
                  background: signRate === 100 ? "#68d391" : signRate > 50 ? "#ecc94b" : "#fc8181",
                }} />
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}